import { defineStore } from 'pinia';
import { ref, computed, watch } from 'vue';
import api from '../services/api';
import { useAuthStore } from './auth';
import { applySavedSection, type SecretsMeta } from '../pages/dashboard/settings/shared/secrets';

export type { SecretMeta, SecretsMeta } from '../pages/dashboard/settings/shared/secrets';

export interface CompanyInfo {
  id: number;
  name: string;
  email: string;
  phone: string | null;
  logo: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  postalCode: string | null;
  timezone: string;
  dateFormat: string;
  timeFormat: string;
  currency: string;
  locale: string;
  fiscalYearStart: number;
}

export interface SecuritySettings {
  passwordMinLength: number;
  passwordRequireUppercase: boolean;
  passwordRequireLowercase: boolean;
  passwordRequireNumbers: boolean;
  passwordRequireSpecialChars: boolean;
  sessionTimeout: number;
  maxLoginAttempts: number;
  twoFactorEnabled: boolean;
  ipRestrictionEnabled: boolean;
  allowedIps: string[];
}

/** Who confirms auto clock-outs (Docs/Attendance_AutoClockOut_Review.md). */
export type AutoClockOutReviewers = 'manager' | 'hr' | 'manager_and_hr';
/** Whether auto-closed hours count before review. */
export type AutoClockOutHoursPolicy = 'scheduled_end' | 'zero';

export interface AttendanceSettings {
  allowClockInBeforeShift: boolean;
  clockInBeforeMinutes: number;
  allowClockOutAfterShift: boolean;
  clockOutAfterMinutes: number;
  autoClockOut: boolean;
  autoClockOutTime: string;
  autoClockOutGraceMinutes: number;
  autoClockOutUseDefaultShift: boolean;
  autoClockOutMaxOpenHours: number;
  autoClockOutReviewers: AutoClockOutReviewers;
  autoClockOutHoursPolicy: AutoClockOutHoursPolicy;
  autoClockOutAllowClaim: boolean;
  autoClockOutReminderHours: number;
  autoClockOutLookbackDays: number;
  requireLocation: boolean;
  allowedRadius: number;
  officeLatitude: number | null;
  officeLongitude: number | null;
  allowRemoteClockIn: boolean;
  requirePhoto: boolean;
  lateThresholdMinutes: number;
  lateDeductionEnabled: boolean;
  lateDeductionType: 'fixed' | 'percentage' | 'hourly_rate';
  lateDeductionAmount: number;
  halfDayThresholdMinutes: number;
  absentThresholdMinutes: number;
  standardWorkHoursPerDay: number;
  standardWorkDaysPerWeek: number;
  overtimeEnabled: boolean;
  overtimeMultiplier: number;
}

export interface InvoiceSettings {
  invoicePrefix: string;
  invoiceStartNumber: number;
  invoiceDueDays: number;
  estimatePrefix: string;
  proposalPrefix: string;
  invoiceTerms: string;
  invoiceNotes: string;
  showLogo: boolean;
  showCompanyAddress: boolean;
}

export interface NotificationSettings {
  emailNotifications: boolean;
  pushNotifications: boolean;
  slackNotifications: boolean;
  /** Write-only: always null from GET /settings (see secretsMeta.notification). */
  slackWebhook: string | null;
  notifyOnNewTask: boolean;
  notifyOnTaskUpdate: boolean;
  notifyOnTaskComplete: boolean;
  notifyOnLeaveRequest: boolean;
  notifyOnLeaveApproval: boolean;
  notifyOnNewTicket: boolean;
  notifyOnTicketReply: boolean;
  digestFrequency: 'instant' | 'hourly' | 'daily' | 'weekly';
}

export interface TaskStatus {
  name: string;
  slug: string;
  color: string;
}

export interface TaskPriority {
  name: string;
  slug: string;
  color: string;
}

export interface ProjectTaskSettings {
  defaultProjectStatus: string;
  defaultTaskStatus: string;
  taskStatuses: TaskStatus[];
  taskPriorities: TaskPriority[];
  requireDueDate: boolean;
  allowTimeTracking: boolean;
  defaultBillableRate: number;
}

export interface TimeLogSettings {
  requireProject: boolean;
  requireTask: boolean;
  minimumLogMinutes: number;
  roundToMinutes: number;
  allowManualEntry: boolean;
  approvalRequired: boolean;
}

export interface FinanceSettings {
  defaultCurrency: string;
  currencyPosition: 'before' | 'after';
  decimalSeparator: string;
  thousandsSeparator: string;
  decimalPlaces: number;
  fiscalYearStart: number;
}

export interface TicketCategory {
  name: string;
  slug: string;
}

export interface TicketSettings {
  ticketPrefix: string;
  autoAssign: boolean;
  defaultAssigneeId: number | null;
  slaEnabled: boolean;
  slaResponseTime: number;
  slaResolutionTime: number;
  categories: TicketCategory[];
}

export interface PaymentGatewaySettings {
  stripeEnabled: boolean;
  stripePublishableKey: string;
  /** Write-only: always null from GET /settings (see secretsMeta.paymentGateway). */
  stripeSecretKey: string | null;
  paypalEnabled: boolean;
  paypalClientId: string;
  /** Write-only: always null from GET /settings. */
  paypalClientSecret: string | null;
  paypalMode: 'sandbox' | 'live';
  offlineEnabled: boolean;
  offlineMethods: Array<{ name: string; instructions: string }>;
}

export interface ExpenseSettings {
  approverUserIds: number[];
  notifyApproversOnSubmit: boolean;
  notifyOwnerOnApproval: boolean;
  notifyOwnerOnRejection: boolean;
}

export interface PayrollSettings {
  paycheckShowLogo: boolean;
  paycheckShowCompanyAddress: boolean;
  paycheckShowBankDetails: boolean;
  paycheckFooterText: string;
  taxDocumentType: string;
  employerTaxId: string;
  autoCalculateAttendance: boolean;
  deductForAbsences: boolean;
  deductForHalfDays: boolean;
  absenceDeductionType: 'full_day' | 'prorated';
  requireApproval: boolean;
  paycheckTemplate: string;
  w2Template: string;
  autoGeneratePaycheck: boolean;
  autoGeneratePayGroupId: number | null;
  // Payroll <-> attendance and overtime (null thresholds = rule off)
  pendingReviewPolicy: PayrollPendingReviewPolicy;
  overtimeWeeklyThresholdHours: number | null;
  overtimeDailyThresholdHours: number | null;
  doubleTimeDailyThresholdHours: number | null;
  doubleTimeMultiplier: number;
  /** 0 = Sunday .. 6 = Saturday */
  workweekStartDay: number;
  overtimeAppliesTo: 'non_exempt' | 'all';
  protectExemptSalary: boolean;
  hourlyPaidHolidays: boolean;
  unpaidBreakMinutes: number;
  unpaidBreakAfterHours: number;
  /** null = standardWorkHoursPerDay / 2 */
  halfDayThresholdHours: number | null;
}

/** How payroll treats attendance still waiting for manager/HR review. */
export type PayrollPendingReviewPolicy = 'block' | 'use_proposed' | 'exclude';

export interface IntegrationSettings {
  googleCalendarEnabled: boolean;
  googleClientId: string;
  slackEnabled: boolean;
  /** Write-only: always null from GET /settings (see secretsMeta.integration). */
  slackBotToken: string | null;
  zoomEnabled: boolean;
  /** Write-only: always null from GET /settings. */
  zoomApiKey: string | null;
}

export interface CustomField {
  module: string;
  name: string;
  type: 'text' | 'number' | 'date' | 'select' | 'checkbox';
  options?: string[];
  required?: boolean;
}

export interface CustomSettings {
  customFields: CustomField[];
}

// Access types of the legacy role payload ({ permissionId, accessType }).
// The Roles & access screens use components/rbac-admin (GET/PATCH
// /settings/access/*) with real catalogue scopes; this type stays for the
// expense hot-fix (utils/expensePermissions.ts).
// - all / owned / added / both: the scope; none: not granted
export type AccessType = 'all' | 'owned' | 'added' | 'both' | 'none';

// Bank / wire transfer details that companies want printed on their
// invoices. Stored in companies.settings (JSONB) on the backend so no
// migration is needed. Every field is optional — partial bank info still
// renders the rows that ARE filled in.
export interface CompanyBankSettings {
  bankName: string;
  accountName: string;
  accountNumber: string;
  routingNumber: string;
  iban: string;
  swiftBic: string;
  bankAddress: string;
  notes: string;
  showOnInvoice: boolean;
}

/**
 * GET /settings (settings.view). Without settings.edit the API returns
 * companyBank, security, paymentGateway and integration as null — tabs
 * must not crash on them. Secrets are always null; their state is in
 * `secretsMeta`.
 */
export interface AllSettings {
  companyInfo: CompanyInfo;
  companyBank: CompanyBankSettings | null;
  security: SecuritySettings | null;
  attendance: AttendanceSettings;
  invoice: InvoiceSettings;
  notification: NotificationSettings;
  projectTask: ProjectTaskSettings;
  timeLog: TimeLogSettings;
  finance: FinanceSettings;
  ticket: TicketSettings;
  paymentGateway: PaymentGatewaySettings | null;
  integration: IntegrationSettings | null;
  custom: CustomSettings;
  expenses: ExpenseSettings;
  payroll: PayrollSettings;
  secretsMeta?: SecretsMeta;
}

/** Sections PATCH /settings/:section accepts (not the secretsMeta descriptor). */
export type SettingsSection = Exclude<keyof AllSettings, 'secretsMeta'>;

/**
 * The copy follows the signed-in person's access (H2): GET /settings answers
 * differently with and without settings.edit (four sections are null
 * without it), so the copy remembers the access it was fetched under.
 *  - settings.edit or the permissions version changes → refetch, keeping
 *    the current copy on screen meanwhile (open forms are not torn down);
 *  - settings.view is lost → the copy is dropped;
 *  - the signed-in user changes → reset (stores/sessionScope.ts); an answer
 *    that started before a reset is dropped.
 */
export const useSettingsStore = defineStore('settings', () => {
  const auth = useAuthStore();

  const settings = ref<AllSettings | null>(null);
  const loading = ref(false);
  const saving = ref(false);
  /** Load errors without a copy (the page's error state) and save errors. */
  const error = ref<string | null>(null);
  /** The last GET /settings failed (also a background refetch that kept the old copy). */
  const loadError = ref<string | null>(null);
  /** accessKey() of the request that produced `settings`. */
  const loadedFor = ref<string | null>(null);

  /** Bumped by $reset: an answer from before belongs to someone else. */
  let generation = 0;
  /** Bumped by every save: a GET that overlapped one may predate it. */
  let writes = 0;
  let inflight: Promise<void> | null = null;

  const canView = () => auth.can('settings.view');
  /** What changes the answer of GET /settings for this person. */
  function accessKey(): string {
    return [auth.user?.id ?? '', auth.permissionsVersion ?? '', canView() ? 'v' : '', auth.can('settings.edit') ? 'e' : ''].join('|');
  }

  const isLoaded = computed(() => settings.value !== null);
  /** The copy was fetched under different access than the current one. */
  const isStale = computed(() => settings.value !== null && loadedFor.value !== accessKey());
  const secretsMeta = computed<SecretsMeta>(() => settings.value?.secretsMeta ?? {});

  async function fetchSettings(): Promise<void> {
    const gen = generation;
    const key = accessKey();
    const writesAtStart = writes;
    loading.value = true;
    error.value = null;
    loadError.value = null;
    try {
      const response = await api.get('/settings');
      if (gen !== generation) return;
      if (writes !== writesAtStart) {
        // A save landed while this was on the wire: read again.
        return await fetchSettings();
      }
      settings.value = response.data.data;
      loadedFor.value = key;
    } catch (err) {
      if (gen !== generation) return;
      loadError.value = 'Failed to load settings';
      // With a copy on screen this is a background refetch, not a save failure.
      if (!settings.value) error.value = loadError.value;
      throw err;
    } finally {
      if (gen === generation) loading.value = false;
    }
  }

  /** Load (or reload a stale copy) once; concurrent callers share the request. Fails soft. */
  async function ensureLoaded(): Promise<void> {
    if (!canView()) return;
    if (isLoaded.value && !isStale.value) return;
    if (inflight) return inflight;
    inflight = fetchSettings()
      .catch(() => { /* error state: `error` */ })
      .finally(() => { inflight = null; });
    return inflight;
  }

  watch(accessKey, (key, previous) => {
    if (key === previous || (!settings.value && !loading.value)) return;
    if (!canView()) {
      $reset();
      return;
    }
    if (loadedFor.value !== key) {
      inflight = null;
      void ensureLoaded();
    }
  });

  /**
   * PATCH one section. Secret fields follow the write-only rule (build the
   * body with buildSectionPayload): absent / '' keeps, a string replaces,
   * null clears. The local copy never holds a secret value — only
   * secretsMeta is updated.
   */
  async function updateSettings<K extends SettingsSection>(section: K, data: Partial<NonNullable<AllSettings[K]>>): Promise<void> {
    saving.value = true;
    error.value = null;
    writes++;
    try {
      await api.patch(`/settings/${section}`, data);
      if (settings.value) {
        const current = settings.value[section] as Record<string, unknown> | null;
        const { values, meta } = applySavedSection(section, current, data as Record<string, unknown>, settings.value.secretsMeta);
        (settings.value as unknown as Record<string, unknown>)[section] = values;
        settings.value.secretsMeta = meta;
      }
    } catch (err) {
      error.value = 'Failed to save settings';
      throw err;
    } finally {
      saving.value = false;
    }
  }

  function $reset(): void {
    generation++;
    inflight = null;
    settings.value = null;
    loadedFor.value = null;
    loading.value = false;
    saving.value = false;
    error.value = null;
    loadError.value = null;
  }

  return {
    settings,
    secretsMeta,
    loading,
    saving,
    error,
    loadError,
    isLoaded,
    isStale,
    fetchSettings,
    ensureLoaded,
    updateSettings,
    $reset,
  };
});
